import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./i18n"; // side-effect: boot i18next before any component renders
import "./styles/globals.css";
import { drain } from "./lib/offlineQueue";
import { clockIn, clockOut } from "./api/time";

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ── PWA: service worker registration + outbox drain ────────────────
//
// Registration only happens in production builds so Vite's HMR path
// isn't shadowed by a stale cache. Drain runs on boot (in case we were
// offline when the user last closed the tab) and whenever the `online`
// event fires.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}

async function replayClockAction(
  action: { kind: "clock-in"; projectCode?: string; notes?: string }
    | { kind: "clock-out"; entryId: string },
): Promise<void> {
  if (action.kind === "clock-in") {
    await clockIn(action.projectCode, action.notes);
  } else {
    await clockOut(action.entryId);
  }
}

function drainOutboxSafely(): void {
  // Fire-and-forget; any surviving items stay queued until the next trigger.
  drain(replayClockAction).catch(() => {
    /* swallow — we'll retry on the next online/drain signal */
  });
}

window.addEventListener("online", drainOutboxSafely);
// Attempt a drain on page load too — covers the case where we were
// offline when the tab was closed.
drainOutboxSafely();

// The service worker asks us to drain when background-sync fires.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "peopleos:drain-outbox") drainOutboxSafely();
  });
}
